import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollText, Download, Eye, Sparkles, Loader2, AlertCircle, CheckCircle2, Package, FileSignature, Copy, X } from "lucide-react";
import { toast } from "sonner";

function pickLatest(documents, templateKey) {
  return (documents || [])
    .filter((d) => d.template_key === templateKey)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
}

const LODGEMENT_CORE = [
  { key: "transfer_power_of_attorney",     label: "Power of Attorney to Pass Transfer",   blurb: "Section 20 Deeds Registries Act — appoints the Conveyancer to pass transfer." },
  { key: "transfer_deed",                  label: "Deed of Transfer",                      blurb: "Prescribed deed prepared by the conveyancer and executed before the Registrar of Deeds." },
  { key: "transfer_seller_declaration",    label: "Seller's Declaration",                  blurb: "Solemn declaration by the Transferor in support of transfer." },
  { key: "transfer_purchaser_declaration", label: "Purchaser's Declaration",               blurb: "Solemn declaration by the Transferee — voetstoots, POPIA, cyber-fraud disclaimer." },
  { key: "transfer_marital_affidavit",     label: "Marital Status Affidavit",              blurb: "Per-party affidavit covering Section 15 MPA spousal consent and accrual." },
];

const LEGAL_COMPLETION = [
  { key: "transfer_duty_declaration",      label: "Transfer Duty Declaration (SARS TDC1)", blurb: "SARS submission per Transfer Duty Act 40 of 1949 — consideration, parties, related-party flags." },
  { key: "transfer_identity_affidavit",    label: "Identity Affidavit",                    blurb: "Per-party affidavit confirming identity and FICA verification (s 21 FICA)." },
  { key: "transfer_fica_affidavit",        label: "FICA Confirmation Affidavit",           blurb: "Conveyancer's affidavit confirming s 21–23 FICA compliance and sanctions screening." },
];

const TRANSFER_TEMPLATES = [...LODGEMENT_CORE, ...LEGAL_COMPLETION];

/**
 * Conveyancing transfer-discipline admin panel — renders only for
 * matter_type ∈ {conveyancing, otp}. Mirrors TrustDocumentActions /
 * OtpDocumentActions structure. Also exposes the LODGEMENT BUNDLE CTA
 * which packages OTP + Transfer drafts + uploaded supporting documents.
 */
export default function TransferDocumentActions({ matter, documents, onChanged }) {
  const mtype = matter?.matter_type || matter?.service_type;
  if (mtype !== "conveyancing" && mtype !== "otp") return null;

  const anyDraft = TRANSFER_TEMPLATES.some((t) => pickLatest(documents, t.key));
  const otpDraftExists = (documents || []).some((d) => d.template_key?.startsWith("otp_"));

  const downloadLodgementBundle = () => {
    api.get(`/admin/matters/${matter.id}/lodgement-bundle.zip`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/zip" });
        const ref  = (matter.id || "").slice(0, 8).toUpperCase();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `EH_Inc_Lodgement_Pack_${ref}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success("Lodgement pack downloaded");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="bg-white border border-zinc-200 mb-5" data-testid="transfer-doc-actions">
      <div className="px-5 py-3 bg-zinc-900 text-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">EH Inc · Transfer Workspace</div>
            <div className="text-sm font-medium">Conveyancing transfer documents</div>
          </div>
        </div>
        {(anyDraft || otpDraftExists) && (
          <button
            type="button"
            onClick={downloadLodgementBundle}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium bg-amber-500 text-zinc-900 hover:bg-amber-400"
            data-testid="download-lodgement-bundle-btn"
          >
            <Package className="h-3.5 w-3.5" />
            Download Conveyancing Lodgement Pack
          </button>
        )}
      </div>

      <div className="px-5 py-2 bg-zinc-50 border-b border-zinc-200 text-[11px] text-zinc-500">
        Templates source: <code className="bg-zinc-100 px-1">standard_deeds_office</code> · prescribed wording per the Deeds Registries Act 47 of 1937.
      </div>

      <div className="px-5 pt-4 pb-1 bg-white border-b border-zinc-100" data-testid="transfer-section-lodgement-core">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Section 1</div>
        <div className="text-sm font-medium text-zinc-900">Lodgement Core · Transfer instruments</div>
      </div>
      <ul className="divide-y divide-zinc-200">
        {LODGEMENT_CORE.map((t) => (
          <TransferRow key={t.key} label={t.label} blurb={t.blurb} templateKey={t.key}
                       matter={matter} doc={pickLatest(documents, t.key)} onChanged={onChanged} />
        ))}
      </ul>

      <div className="px-5 pt-4 pb-1 bg-white border-t border-b border-zinc-100" data-testid="transfer-section-legal-completion">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Section 2</div>
        <div className="text-sm font-medium text-zinc-900">SARS &amp; FICA Legal Completion</div>
      </div>
      <ul className="divide-y divide-zinc-200">
        {LEGAL_COMPLETION.map((t) => (
          <TransferRow key={t.key} label={t.label} blurb={t.blurb} templateKey={t.key}
                       matter={matter} doc={pickLatest(documents, t.key)} onChanged={onChanged} />
        ))}
      </ul>
    </div>
  );
}


function TransferRow({ label, blurb, templateKey, matter, doc, onChanged }) {
  const [busy, setBusy]       = useState(false);
  const [missing, setMissing] = useState(null);
  const [signReq, setSignReq] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);

  const isSigningRequired = templateKey.startsWith("transfer_") || templateKey === "otp_main";

  // Auto-load existing sign request once a doc exists
  useEffect(() => {
    if (!doc?.id || !isSigningRequired) { setSignReq(null); return; }
    api.get(`/admin/matters/${matter.id}/documents/${doc.id}/sign-request`)
      .then((res) => setSignReq(res.data || null))
      .catch(() => setSignReq(null));
  }, [doc?.id, matter?.id, isSigningRequired]);

  const generate = async () => {
    setBusy(true);
    setMissing(null);
    try {
      const { data } = await api.post(
        `/admin/matters/${matter.id}/documents/generate-structured`,
        { template_key: templateKey },
      );
      toast.success(`${label} generated`, { description: data?.audit_event?.summary || "Draft ready for review." });
      onChanged?.();
    } catch (e) {
      const body = e?.response?.data;
      if (e?.response?.status === 422 && body?.blocked) {
        setMissing(body.missing || []);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const fetchPdf = (cb) => {
    if (!doc) return;
    api.get(`/admin/documents/${doc.id}/pdf`, { responseType: "blob" })
      .then((res) => cb(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))))
      .catch((e) => toast.error(formatApiError(e)));
  };

  const signStatusChip = (() => {
    if (!signReq) return null;
    const map = {
      sent:             { label: "Sent for signature",   chip: "bg-amber-100 text-amber-900" },
      partially_signed: { label: "Partially signed",     chip: "bg-sky-100 text-sky-900" },
      fully_signed:     { label: "Signed by all parties", chip: "bg-emerald-100 text-emerald-900" },
      draft:            { label: "Draft",                 chip: "bg-zinc-100 text-zinc-700" },
    };
    return map[signReq.status] || null;
  })();

  return (
    <li className="p-5" data-testid={`transfer-row-${templateKey}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-medium text-sm text-zinc-900">{label}</div>
          <div className="text-xs text-zinc-500 mt-1">{blurb}</div>
          {doc && (
            <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> Draft generated
              </span>
              <span className="text-zinc-400">·</span>
              <span>{new Date(doc.created_at).toLocaleString()}</span>
              <span className="text-zinc-400">·</span>
              <span className="uppercase tracking-wider text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5">
                {(doc.status || "").replace(/_/g, " ")}
              </span>
              {signStatusChip && (
                <>
                  <span className="text-zinc-400">·</span>
                  <span className={`uppercase tracking-wider text-[10px] px-1.5 py-0.5 ${signStatusChip.chip}`}
                        data-testid={`sign-status-${templateKey}`}>
                    {signStatusChip.label}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {doc ? (
            <>
              <Button variant="outline" size="sm" className="rounded-none"
                onClick={() => fetchPdf((u) => { window.open(u, "_blank", "noopener,noreferrer"); setTimeout(() => URL.revokeObjectURL(u), 60_000); })}
                data-testid={`preview-${templateKey}-btn`}>
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
              </Button>
              <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                onClick={() => fetchPdf((u) => { const a = document.createElement("a"); a.href = u; a.download = `${(doc.template_name || label).replace(/\s+/g, "_")}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u); })}
                data-testid={`download-${templateKey}-btn`}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download
              </Button>
              {isSigningRequired && (
                <Button size="sm" variant="outline" className="rounded-none"
                  onClick={() => setShowSignModal(true)}
                  data-testid={`send-sign-${templateKey}-btn`}>
                  <FileSignature className="h-3.5 w-3.5 mr-1.5" />
                  {signReq ? "Sign links" : "Send for signature"}
                </Button>
              )}
              <Link to={`/admin/matters/${matter.id}/documents/${doc.id}`}
                className="text-xs text-zinc-500 hover:text-zinc-900 px-2"
                data-testid={`open-${templateKey}-workspace`}>
                Workspace →
              </Link>
            </>
          ) : (
            <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800" onClick={generate} disabled={busy}
              data-testid={`generate-${templateKey}-btn`}>
              {busy
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate</>}
            </Button>
          )}
        </div>
      </div>

      {missing && missing.length > 0 && (
        <div className="mt-3 border border-amber-300 bg-amber-50 p-3" data-testid={`transfer-missing-${templateKey}`}>
          <div className="text-xs font-medium text-amber-900 flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            Complete required fields to generate {label}
          </div>
          <ul className="text-[12px] text-amber-800 list-disc pl-5 mt-1.5 space-y-0.5">
            {missing.map((f) => <li key={f}>{f}</li>)}
          </ul>
        </div>
      )}

      {showSignModal && doc && (
        <SignRequestModal
          matter={matter}
          doc={doc}
          templateKey={templateKey}
          existing={signReq}
          onClose={() => setShowSignModal(false)}
          onChanged={(r) => { setSignReq(r); onChanged?.(); }}
        />
      )}
    </li>
  );
}


function SignRequestModal({ matter, doc, templateKey, existing, onClose, onChanged }) {
  // Suggest parties from matter entities
  const persons = (matter?.intake_data?.persons || matter?.entities?.persons || []);
  const defaults = persons
    .filter((p) => ["seller", "purchaser"].includes(p.role))
    .map((p) => ({ role: p.role, name: p.full_name || "", email: p.email || "" }));

  const [parties, setParties] = useState(
    existing?.parties?.length
      ? existing.parties.map((p) => ({ role: p.role, name: p.name, email: p.email || "" }))
      : (defaults.length ? defaults : [{ role: "seller", name: "", email: "" }])
  );
  const [submitting, setSubmitting] = useState(false);

  const send = async () => {
    setSubmitting(true);
    try {
      const cleaned = parties
        .filter((p) => p.name?.trim())
        .map((p) => ({ role: p.role, name: p.name.trim(), email: p.email?.trim() || null }));
      if (!cleaned.length) {
        toast.error("Add at least one party with a name");
        setSubmitting(false);
        return;
      }
      const res = await api.post(
        `/admin/matters/${matter.id}/documents/${doc.id}/sign-request`,
        { parties: cleaned },
      );
      toast.success(`Sign request sent to ${cleaned.length} parties`);
      onChanged?.(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const baseSignUrl = `${window.location.origin}/sign`;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" data-testid="sign-modal">
      <div className="bg-white border border-zinc-200 max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 bg-zinc-900 text-white flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">Signing</div>
            <div className="text-sm font-medium">{doc.template_name || templateKey}</div>
          </div>
          <button onClick={onClose} className="text-zinc-300 hover:text-white" data-testid="sign-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {existing ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Per-party sign links</div>
              <ul className="space-y-2">
                {(existing.parties || []).map((p) => (
                  <li key={p.id} className="border border-zinc-200 p-3" data-testid={`sign-party-${p.role}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium text-zinc-900">{p.name}</div>
                        <div className="text-[11px] text-zinc-500">{p.role} {p.email ? `· ${p.email}` : ""}</div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${p.status === "signed" ? "bg-emerald-100 text-emerald-900" : "bg-zinc-100 text-zinc-700"}`}>
                        {p.status}
                      </span>
                    </div>
                    {p.sign_token && p.status !== "signed" && (
                      <div className="mt-2 flex items-center gap-2">
                        <code className="text-[10px] bg-zinc-50 px-2 py-1 flex-1 truncate border border-zinc-200">
                          {baseSignUrl}/{p.sign_token}
                        </code>
                        <button
                          className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50"
                          onClick={() => { navigator.clipboard.writeText(`${baseSignUrl}/${p.sign_token}`); toast.success("Sign link copied"); }}
                          data-testid={`copy-sign-${p.role}`}
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    {p.signed_at && (
                      <div className="text-[11px] text-emerald-700 mt-1">Signed: {new Date(p.signed_at).toLocaleString()}</div>
                    )}
                  </li>
                ))}
              </ul>
              <Button variant="outline" size="sm" className="rounded-none"
                      onClick={() => onChanged?.(null) /* clear so the form re-renders */}>
                Replace sign request
              </Button>
            </>
          ) : (
            <>
              <div className="text-xs text-zinc-600">
                Add the parties who must sign this document. Each will receive a unique signing link.
              </div>
              {parties.map((p, idx) => (
                <div key={idx} className="border border-zinc-200 p-3 space-y-2 relative">
                  <button
                    onClick={() => setParties(parties.filter((_, i) => i !== idx))}
                    className="absolute right-2 top-2 text-zinc-400 hover:text-red-600"
                    aria-label="Remove party"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-zinc-500">Role
                      <select
                        className="block w-full text-xs border border-zinc-300 rounded-none px-2 py-1.5 mt-1 bg-white"
                        value={p.role}
                        onChange={(e) => setParties(parties.map((x, i) => i === idx ? { ...x, role: e.target.value } : x))}
                        data-testid={`sign-party-role-${idx}`}
                      >
                        <option value="seller">Seller</option>
                        <option value="purchaser">Purchaser</option>
                        <option value="witness">Witness</option>
                        <option value="commissioner">Commissioner of Oaths</option>
                      </select>
                    </label>
                    <label className="text-xs text-zinc-500">Email (optional)
                      <Input type="email" value={p.email} className="mt-1 rounded-none h-8 text-xs"
                        onChange={(e) => setParties(parties.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))}
                        data-testid={`sign-party-email-${idx}`}
                      />
                    </label>
                  </div>
                  <label className="text-xs text-zinc-500 block">Full name
                    <Input type="text" value={p.name} className="mt-1 rounded-none h-8 text-xs"
                      onChange={(e) => setParties(parties.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                      data-testid={`sign-party-name-${idx}`}
                    />
                  </label>
                </div>
              ))}
              <Button variant="outline" size="sm" className="rounded-none"
                onClick={() => setParties([...parties, { role: "purchaser", name: "", email: "" }])}
                data-testid="sign-add-party">
                + Add party
              </Button>
            </>
          )}
        </div>

        {!existing && (
          <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-200 flex justify-end gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={onClose}>Cancel</Button>
            <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                    onClick={send} disabled={submitting}
                    data-testid="sign-modal-send-btn">
              {submitting
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</>
                : <><FileSignature className="h-3.5 w-3.5 mr-1.5" /> Send for signature</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
